// Keyboard-first: WhatsApp Web's shortcuts, a command palette (Ctrl/⌘+K)
// that jumps to any chat or runs any action, the shortcuts sheet (Ctrl/⌘+/)
// and the composer's slash commands (/poll, /event, /call…).
import { signal } from "@preact/signals";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { addingAccount } from "../state";
import { getAccounts, switchAccount } from "../api";
import * as S from "../state";
import { startCall } from "./Call";
import { sharing } from "./chatui";
import { Icon } from "./Icon";
import { recordingNote } from "./VideoNote";
import { toggleLock, visibleChat } from "./ChatLock";
import { drawer, engine, infoOpen, toast } from "./Main";

export const palette = signal(false);
export const shortcutsHelp = signal(false);
/** The composer opens its photo / document picker when this is set. */
export const attachRequest = signal<"media" | "docs" | null>(null);

const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
export const MOD = isMac ? "⌘" : "Ctrl";
const ALT = isMac ? "⌥" : "Alt";

// ---- theme -------------------------------------------------------------------------

type Theme = "system" | "light" | "dark";
export function applyTheme(t?: Theme) {
  let theme = t;
  try {
    theme ??= (localStorage.getItem("xmm.theme") as Theme | null) ?? "system";
    localStorage.setItem("xmm.theme", theme);
  } catch {
    theme ??= "system";
  }
  if (theme === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
}
function cycleTheme() {
  const cur = (document.documentElement.dataset.theme as Theme | undefined) ?? "system";
  const next: Theme = cur === "system" ? "dark" : cur === "dark" ? "light" : "system";
  applyTheme(next);
  toast(`Theme: ${next}`);
}

// ---- actions -----------------------------------------------------------------------

export interface Action {
  id: string;
  label: string;
  keys?: string; // shown, e.g. "Ctrl Alt N"
  icon?: string;
  group: "Chat" | "Go to" | "Create" | "App";
  run: () => void;
}

const active = () => S.activeChat.value;
const isPerson = (j: string) => !S.isGroup(j) && !S.isChannel(j) && !S.isBroadcast(j);

function focusSearch() {
  const find = () => document.querySelector('input[aria-label="Search chats and messages"]') as HTMLInputElement | null;
  if (drawer.value === "none" && S.tab.value === "chats" && find()) return find()!.focus();
  drawer.value = "none";
  S.tab.value = "chats";
  setTimeout(() => find()?.focus()); // once the chat list is back
}

/** Next / previous chat in the list, in the order it's shown. */
function stepChat(dir: 1 | -1) {
  const rows = [...document.querySelectorAll<HTMLElement>(".chat-row[data-jid]")].map((r) => r.dataset.jid!);
  if (!rows.length) return;
  const at = rows.indexOf(active() ?? "");
  const next = rows[at < 0 ? (dir > 0 ? 0 : rows.length - 1) : (at + dir + rows.length) % rows.length];
  void engine.value?.openChat(next);
}

function setChat(p: { pinned?: boolean; archived?: boolean; muted_until?: number }, done: string) {
  const j = active();
  if (!j) return toast("Open a chat first");
  void engine.value?.setChat(j, p).then(
    () => toast(done),
    (e) => toast(String((e as Error).message ?? e)),
  );
}

/** Everything the palette offers, for the current state. */
export function actions(): Action[] {
  const j = active();
  const out: Action[] = [
    { id: "palette", label: "Command palette", keys: `${MOD} K`, group: "App", icon: "search", run: () => (palette.value = true) },
    { id: "help", label: "Keyboard shortcuts", keys: `${MOD} /`, group: "App", icon: "info", run: () => (shortcutsHelp.value = true) },
    { id: "search", label: "Search chats", keys: `${MOD} ${ALT} /`, group: "Go to", icon: "search", run: focusSearch },
    { id: "next", label: "Next chat", keys: `${ALT} ↓`, group: "Go to", icon: "down", run: () => stepChat(1) },
    { id: "prev", label: "Previous chat", keys: `${ALT} ↑`, group: "Go to", icon: "down", run: () => stepChat(-1) },
    { id: "new-chat", label: "New chat", keys: `${MOD} ${ALT} N`, group: "Create", icon: "chat", run: () => (drawer.value = "newChat") },
    { id: "new-group", label: "New group", keys: `${MOD} ${ALT} Shift N`, group: "Create", icon: "group", run: () => (drawer.value = "newGroup") },
    { id: "new-broadcast", label: "New broadcast list", group: "Create", icon: "campaign", run: () => (drawer.value = "newBroadcast") },
    { id: "new-community", label: "New community", group: "Create", icon: "group", run: () => (drawer.value = "newCommunity") },
    { id: "profile", label: "Profile", keys: `${MOD} ${ALT} P`, group: "Go to", icon: "person", run: () => (drawer.value = "profile") },
    { id: "privacy", label: "Privacy & settings", keys: `${MOD} ${ALT} ,`, group: "Go to", icon: "lock", run: () => (drawer.value = "privacy") },
    { id: "devices", label: "Linked devices", group: "Go to", icon: "devices", run: () => (drawer.value = "devices") },
    { id: "backup", label: "Chat backup", group: "Go to", icon: "download", run: () => (drawer.value = "backup") },
    { id: "calls", label: "Calls", group: "Go to", icon: "phone", run: () => (drawer.value = "calls") },
    { id: "starred", label: "Starred messages", group: "Go to", icon: "star", run: () => (drawer.value = "starred") },
    { id: "archived", label: "Archived chats", group: "Go to", icon: "archive", run: () => (drawer.value = "archived") },
    { id: "status", label: "Status", group: "Go to", icon: "status", run: () => ((drawer.value = "none"), (S.tab.value = "status")) },
    { id: "channels", label: "Channels", group: "Go to", icon: "campaign", run: () => ((drawer.value = "none"), (S.tab.value = "channels")) },
    { id: "storage", label: "Manage storage", group: "Go to", icon: "document", run: () => (drawer.value = "storage") },
    { id: "appearance", label: "Appearance: theme, chat background, bubble colours", group: "Go to", icon: "smiley", run: () => (drawer.value = "appearance") },
    { id: "theme", label: "Switch theme (system / dark / light)", keys: `${MOD} ${ALT} Shift D`, group: "App", icon: "smiley", run: cycleTheme },
    ...getAccounts()
      .filter((a) => a.xmpp.jid !== S.myJid.value)
      .map((a) => ({ id: "acct-" + a.xmpp.jid, label: `Switch to ${a.user.name || a.user.phone}`, group: "App" as const, icon: "person", run: () => switchAccount(a.xmpp.jid) })),
    { id: "add-account", label: "Add account", group: "App", icon: "personAdd", run: () => (addingAccount.value = true) },
  ];
  if (j) {
    const name = S.displayName(j);
    out.push(
      { id: "info", label: `${S.isGroup(j) ? "Group" : "Contact"} info: ${name}`, keys: `${MOD} ${ALT} I`, group: "Chat", icon: "info", run: () => (infoOpen.value = !infoOpen.value) },
      { id: "archive", label: S.isArchived(j) ? "Unarchive chat" : "Archive chat", keys: `${MOD} ${ALT} E`, group: "Chat", icon: "archive", run: () => setChat({ archived: !S.isArchived(j) }, S.isArchived(j) ? "Chat unarchived" : "Chat archived") },
      { id: "pin", label: S.pinnedAt(j) ? "Unpin chat" : "Pin chat", keys: `${MOD} ${ALT} Shift P`, group: "Chat", icon: "pin", run: () => setChat({ pinned: !S.pinnedAt(j) }, S.pinnedAt(j) ? "Chat unpinned" : "Chat pinned") },
      {
        id: "mute",
        label: S.isMuted(j) ? "Unmute chat" : "Mute chat (8 hours)",
        keys: `${MOD} ${ALT} Shift M`,
        group: "Chat",
        icon: "bellOff",
        run: () => setChat({ muted_until: S.isMuted(j) ? 0 : Math.floor(Date.now() / 1000) + 8 * 3600 }, S.isMuted(j) ? "Chat unmuted" : "Muted for 8 hours"),
      },
    );
    out.push({ id: "export", label: "Export chat", group: "Chat", icon: "download", run: () => void import("./Export").then((x) => x.exportChat(j, false)) });
    out.push({ id: "lock", label: S.lockedChats.value.has(j) ? "Unlock chat" : "Lock chat", group: "Chat", icon: "lock", run: () => toggleLock(j) });
    if (!S.isChannel(j)) {
      out.push(
        { id: "photo", label: "Send photos or videos", group: "Chat", icon: "image", run: () => (attachRequest.value = "media") },
        { id: "doc", label: "Send a document", group: "Chat", icon: "document", run: () => (attachRequest.value = "docs") },
        { id: "videonote", label: "Record a video note", group: "Chat", icon: "videocam", run: () => (recordingNote.value = j) },
        { id: "location", label: "Send your location", group: "Chat", icon: "pin", run: () => (sharing.value = "location") },
        { id: "contact", label: "Send a contact", group: "Chat", icon: "person", run: () => (sharing.value = "contact") },
      );
      if (!S.isBroadcast(j))
        out.push(
          { id: "poll", label: "Create a poll", group: "Chat", icon: "poll", run: () => (sharing.value = "poll") },
          { id: "event", label: "Create an event", group: "Chat", icon: "event", run: () => (sharing.value = "event") },
        );
    }
    if (isPerson(j) || S.isGroup(j))
      out.push(
        { id: "voice", label: `Voice call ${name}`, group: "Chat", icon: "phone", run: () => startCall(j, "audio") },
        { id: "video", label: `Video call ${name}`, group: "Chat", icon: "videocam", run: () => startCall(j, "video") },
      );
  }
  return out;
}

// ---- the global key map ------------------------------------------------------------

const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));

function run(id: string) {
  actions()
    .find((a) => a.id === id)
    ?.run();
}

/** Keys, matched on e.code so ⌥ on a Mac (which changes e.key) still works. */
export function useShortcuts() {
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;
      // The numpad's "/" counts as "/" too.
      const k = e.code === "NumpadDivide" ? "Slash" : e.code;
      let id: string | null = null;
      if (mod && !e.altKey && !e.shiftKey && k === "KeyK") id = "palette";
      else if (mod && !e.altKey && k === "Slash") id = "help";
      else if (mod && e.altKey) {
        if (e.shiftKey) id = { KeyN: "new-group", KeyP: "pin", KeyM: "mute", KeyD: "theme" }[k] ?? null;
        else id = { KeyN: "new-chat", Slash: "search", KeyP: "profile", Comma: "privacy", KeyE: "archive", KeyI: "info", Tab: "next" }[k] ?? null;
      } else if (e.altKey && !mod && !e.shiftKey && (k === "ArrowDown" || k === "ArrowUp")) id = k === "ArrowDown" ? "next" : "prev";
      else if (!typing(e.target) && !mod && !e.altKey) {
        // Outside text fields: "/" searches, "?" shows the shortcuts.
        if (e.key === "/") id = "search";
        else if (e.key === "?") id = "help";
      }
      if (!id) return;
      e.preventDefault();
      run(id);
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);
}

// ---- command palette ------------------------------------------------------------------

type Item = { key: string; label: string; hint?: string; icon: string; group: string; run: () => void };

function score(label: string, q: string) {
  const l = label.toLowerCase();
  if (!q) return 1;
  if (l.startsWith(q)) return 3;
  if (l.split(/\s+/).some((w) => w.startsWith(q))) return 2;
  // all characters in order ("ngr" ~ "New group")
  let i = 0;
  for (const c of l) if (c === q[i]) i++;
  return i === q.length ? 1 : l.includes(q) ? 1 : 0;
}

export function CommandPalette() {
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const list = useRef<HTMLUListElement>(null);
  const input = useRef<HTMLInputElement>(null);
  // (autoFocus only applies on page load.) Focus before the next key is
  // handled, so text typed right after Ctrl+K lands here, not in the chat.
  useLayoutEffect(() => input.current?.focus(), []);
  const close = () => (palette.value = false);
  const items = useMemo<Item[]>(() => {
    const query = q.trim().toLowerCase();
    const chats: Item[] = S.chats.value.filter((c) => visibleChat(c.jid)).map((c) => ({
      key: "chat:" + c.jid,
      label: S.displayName(c.jid),
      icon: S.isGroup(c.jid) ? "group" : S.isChannel(c.jid) ? "campaign" : "person",
      group: "Chats",
      run: () => void engine.value?.openChat(c.jid),
    }));
    const acts: Item[] = actions()
      .filter((a) => a.id !== "palette")
      .map((a) => ({ key: a.id, label: a.label, hint: a.keys, icon: a.icon ?? "arrowRight", group: a.group, run: a.run }));
    // Empty: this chat's actions, then the people you message most.
    const frequent = S.frequent.value.map((j) => chats.find((c) => c.key === "chat:" + j)).filter((c): c is Item => !!c).map((c) => ({ ...c, group: "Frequent" }));
    const top = frequent.length ? frequent.slice(0, 6) : chats.slice(0, 6);
    const all = query ? [...chats, ...acts] : [...acts.filter((a) => a.group === "Chat"), ...top, ...acts.filter((a) => a.group !== "Chat")];
    return all
      .map((x) => ({ x, s: score(x.label, query) }))
      .filter((r) => r.s > 0)
      .sort((a, b) => (query ? b.s - a.s : 0))
      .map((r) => r.x)
      .slice(0, 40);
  }, [q, S.chats.value, S.activeChat.value]);
  useEffect(() => setAt(0), [q]);
  useEffect(() => list.current?.querySelector(".on")?.scrollIntoView({ block: "nearest" }), [at]);
  const pick = (it: Item) => {
    close();
    it.run();
  };
  return (
    <div class="modal-backdrop palette-backdrop" onClick={close}>
      <div class="palette" role="dialog" aria-label="Command palette" onClick={(e) => e.stopPropagation()}>
        <div class="palette-input">
          <Icon name="search" size={18} />
          <input
            ref={input}
            value={q}
            placeholder="Type a chat or a command…"
            aria-label="Command"
            onInput={(e) => setQ(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") (e.preventDefault(), setAt((a) => Math.min(items.length - 1, a + 1)));
              else if (e.key === "ArrowUp") (e.preventDefault(), setAt((a) => Math.max(0, a - 1)));
              else if (e.key === "Enter" && items[at]) (e.preventDefault(), pick(items[at]));
              else if (e.key === "Escape") (e.preventDefault(), close());
            }}
          />
          <kbd>Esc</kbd>
        </div>
        <ul class="palette-list" role="listbox" ref={list}>
          {items.map((it, i) => (
            <li key={it.key} role="option" aria-selected={i === at} class={i === at ? "on" : ""} onMouseMove={() => setAt(i)} onClick={() => pick(it)}>
              <Icon name={it.icon as "search"} size={18} />
              <span class="grow ellipsis">{it.label}</span>
              {it.hint ? <Keys keys={it.hint} /> : <small class="muted">{it.group}</small>}
            </li>
          ))}
          {!items.length && <li class="empty">Nothing matches “{q}”</li>}
        </ul>
      </div>
    </div>
  );
}

export function Keys({ keys }: { keys: string }) {
  return (
    <span class="keys">
      {keys.split(" ").map((k, i) => (
        <kbd key={i}>{k}</kbd>
      ))}
    </span>
  );
}

// ---- shortcuts sheet ------------------------------------------------------------------

const COMPOSER_KEYS: [string, string][] = [
  ["Enter", "Send"],
  ["Shift Enter", "New line"],
  ["↑", "Edit your last message (empty box)"],
  ["Esc", "Cancel reply / edit"],
  ["/", "Slash commands: /poll /event /call …"],
  ["@", "Mention someone (groups)"],
];

export function ShortcutsHelp() {
  const close = () => (shortcutsHelp.value = false);
  // A layout effect: Escape works the moment the sheet is up.
  useLayoutEffect(() => {
    const on = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);
  const all = actions().filter((a) => a.keys);
  return (
    <div class="modal-backdrop" onClick={close}>
      <div class="modal shortcuts" role="dialog" aria-label="Keyboard shortcuts" onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={close} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>Keyboard shortcuts</h2>
        </header>
        <div class="shortcuts-grid">
          {all.map((a) => (
            <div key={a.id} class="shortcut">
              <span>{a.label.replace(/: .*$/, "")}</span>
              <Keys keys={a.keys!} />
            </div>
          ))}
          <h3>In the message box</h3>
          {COMPOSER_KEYS.map(([k, label]) => (
            <div key={k} class="shortcut">
              <span>{label}</span>
              <Keys keys={k} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- slash commands (composer) --------------------------------------------------------

export interface Slash {
  cmd: string;
  label: string;
  icon: string;
  run: () => void;
}

/** "/poll", "/event"… for the open chat (what its attachment menu offers, and calls). */
export function slashCommands(jid: string): Slash[] {
  const out: Slash[] = [];
  const extras = !S.isChannel(jid) && !S.isBroadcast(jid);
  if (extras) out.push({ cmd: "poll", label: "Create a poll", icon: "poll", run: () => (sharing.value = "poll") });
  if (extras) out.push({ cmd: "event", label: "Create an event", icon: "event", run: () => (sharing.value = "event") });
  out.push(
    { cmd: "photo", label: "Send photos or videos", icon: "image", run: () => (attachRequest.value = "media") },
    { cmd: "file", label: "Send a document", icon: "document", run: () => (attachRequest.value = "docs") },
    { cmd: "videonote", label: "Record a video note", icon: "videocam", run: () => (recordingNote.value = jid) },
    { cmd: "location", label: "Send your location", icon: "pin", run: () => (sharing.value = "location") },
    { cmd: "contact", label: "Send a contact", icon: "person", run: () => (sharing.value = "contact") },
  );
  if (isPerson(jid) || S.isGroup(jid))
    out.push(
      { cmd: "call", label: "Start a voice call", icon: "phone", run: () => startCall(jid, "audio") },
      { cmd: "video", label: "Start a video call", icon: "videocam", run: () => startCall(jid, "video") },
    );
  return out;
}
